import PropTypes from "prop-types";
import { forwardRef, useRef, useState, useMemo, useCallback, useEffect } from "react";
import { FormBuilder } from "@repo/design-system/components/forms";
import "./PageHeader.scss";
import { useTranslation } from "react-i18next";
import { PageHeaderConfig } from "./PageConfig.data";
import { ButtonGroup, Iconify, ButtonVariant, Button } from "@repo/design-system/components/ui";

export const PageHeader = forwardRef(({
    title, description, headerIcon, children,
    searchConfig = {},
    filterConfig = {},
    exportConfig = {},
    customButton = [],
    rightContent,
}, ref) => {

    const formRef = useRef();
    const filterRef = useRef();
    const { t } = useTranslation();
    const [filters, setFilters] = useState();
    const [searchData, setSearchData] = useState({});
    const [showFilterPanel, setShowFilterPanel] = useState(filterConfig?.defaultFilterOpen ?? true);

    const [showSearchBar, setShowSearchBar] = useState(false); // mobile search toggle

    const {
        isEnabledExport = false,
        onExport,
        exportLabel = "",
        isLoading: isExportLoading = false,
        exportOptions = [],   // [{ label, icon, onClick }]
        ...exportOtherConfig
    } = exportConfig;

    const [showExportMenu, setShowExportMenu] = useState(false);
    const exportMenuRef = useRef();

    // Close dropdown when clicking outside
    useEffect(() => {
        if (!showExportMenu) return;
        const handler = (e) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
                setShowExportMenu(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [showExportMenu]);

    const {
        filterFormConfig = [], isEnabledFilter = false, onFilterSearch,
        filterPanelSectionClass = "col-lg-4 col-md-5",
        filterbuttonConfig = {},
        defaultFilterOpen = true,
    } = filterConfig;
    const { onSearch, searchFormConfig = [], isEnabledSearch = false } = searchConfig;

    //  Merge default config + external config + state
    const headerConfig = useMemo(() => {
        const baseConfig = PageHeaderConfig(t);

        return {
            ...baseConfig,
            ...searchFormConfig, // allow override from props
            initialState: {
                ...baseConfig.initialState,
                ...(searchFormConfig?.initialState || {}),
                ...searchData,
            },
        };
    }, [t, searchFormConfig, searchData]);

    //  Merge default config + external config + state
    const filterFormConfiguration = useMemo(() => {
        if (isEnabledFilter) {
            const baseConfig = filterFormConfig?.(t);

            return {
                ...baseConfig,
                initialState: {
                    ...baseConfig.initialState,
                    ...filters,
                },
            };
        }
    }, [t, filterFormConfig, filters]);

    // Handle Search
    const handleSearch = useCallback(() => {
        if (!formRef.current) return;

        const formData = formRef.current.getValidatedFormData?.();
        if (!formData) return;

        setSearchData(formData);
        onSearch?.(formData);
    }, [onSearch]);

    // Handle Reset
    const handleSearchReset = useCallback(() => {
        setSearchData({});
        onSearch?.({});
    }, [onSearch]);

    const handleFilterSearch = useCallback(() => {
        if (!filterRef.current) return;
        const formData = filterRef.current.getValidatedFormData?.();
        if (!formData) return;
        setFilters(formData);
        onFilterSearch?.(formData);
    }, [onFilterSearch]);

    const handleFilterRest = useCallback(() => {
        setFilters({});
        onFilterSearch?.({});
    }, [onFilterSearch]);

    return (
        <div className="page-header" ref={ref}>
            <div className="page-header-section">

                <div className="page-header-left-info">
                    {headerIcon && (
                        <Iconify className="font-icon" icon={headerIcon} />
                    )}
                    <div className="page-header-text">
                        {title && <h3 className="page-header-title">{title}</h3>}
                        {description && <p className="page-header-description">{description}</p>}
                    </div>
                </div>

                <div className="page-header-right-controls">
                    {isEnabledSearch && (
                        <div className="page-header-search">
                            <FormBuilder ref={formRef} config={headerConfig} />
                            <ButtonGroup
                                primaryBtnObj={{
                                    label: t("Search"),
                                    leftIcon: "icon-park-outline:search",
                                    variant: ButtonVariant.PRIMARY,
                                    onClick: handleSearch,
                                }}
                                secondaryBtnObj={{
                                    label: t("Reset"),
                                    leftIcon: "material-symbols:close-rounded",
                                    variant: ButtonVariant.OUTLINE,
                                    onClick: handleSearchReset,
                                }}
                            />
                        </div>
                    )}

                    {isEnabledExport && (
                        <div className="page-header-export" ref={exportMenuRef}>
                            <Button variant={ButtonVariant.OUTLINE}
                                // iconWidth="18px"
                                leftIcon="material-symbols-light:file-export-rounded"
                                loading={isExportLoading}
                                {...exportOtherConfig}
                                onClick={exportOptions.length > 0
                                    ? () => setShowExportMenu((prev) => !prev)
                                    : onExport
                                }
                                rightIcon={exportOptions.length > 0 ? "mdi:chevron-down" : undefined}>
                                {exportLabel}
                            </Button>

                            {exportOptions.length > 0 && showExportMenu && (
                                <div className="export-dropdown">
                                    {exportOptions.map((option, idx) => (
                                        <button key={idx} className="export-dropdown-item"
                                            onClick={() => {
                                                option.onClick?.();
                                                setShowExportMenu(false);
                                            }}>
                                            {option.icon && (
                                                <Iconify icon={option.icon}
                                                    width={16}
                                                    className="export-dropdown-icon" />
                                            )}
                                            <span>{option.label}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {isEnabledFilter && (
                        <div className="page-header-filter">
                            <Button
                                className={`page-header-filter-btn ${showFilterPanel ? "active" : ""}`}
                                variant={ButtonVariant.PRIMARY}
                                ttLabel="Filter"
                                targetId="filter"
                                onClick={() => setShowFilterPanel((prev) => !prev)}
                                rightIcon="mdi:filter"
                                iconWidth={22}
                            />
                        </div>
                    )}

                    {customButton?.length > 0 && (
                        <div className="page-header-custom-btn">
                            {customButton.map((btn, index) => (
                                <Button
                                    key={index}
                                    className={`page-header-custom-btn-item ${btn?.className ?? ""}`}
                                    variant={btn?.variant}
                                    ttLabel={btn?.ttLabel}
                                    targetId={btn?.targetId}
                                    onClick={btn?.onClick}
                                    leftIcon={btn?.leftIcon}
                                    size={btn?.size}
                                    loading={btn?.loading}
                                    rightIcon={btn?.rightIcon}
                                    iconWidth={btn?.iconWidth}
                                >
                                    {btn?.label}
                                </Button>
                            ))}
                        </div>
                    )}

                    {rightContent}
                </div>
            </div>

            {showFilterPanel && isEnabledFilter &&
                <div className="ph-filter-panel-section">
                    <div className="row align-items-start">
                        <FormBuilder ref={filterRef} config={filterFormConfiguration} />
                        <ButtonGroup
                            sectionClass={`cmt-14 ${filterPanelSectionClass} justify-content-start`}
                            primaryBtnObj={{
                                leftIcon: filterbuttonConfig.primaryBtnObj.leftIcon || "charm:tick",
                                ...filterbuttonConfig.primaryBtnObj,
                                onClick: handleFilterSearch,
                            }}
                            secondaryBtnObj={{
                                leftIcon: filterbuttonConfig.secondaryBtnObj.leftIcon || "material-symbols:close-rounded",
                                variant: filterbuttonConfig.secondaryBtnObj.variant || ButtonVariant.OUTLINE,
                                ...filterbuttonConfig?.secondaryBtnObj,
                                onClick: handleFilterRest,
                            }}
                        />
                    </div>
                </div>
            }

            {children && (
                <div className="page-header-children">{children}</div>
            )}
        </div>
    );
});

PageHeader.propTypes = {
    title: PropTypes.string.isRequired,
    headerIcon: PropTypes.string,
    children: PropTypes.node,
    searchConfig: PropTypes.shape({
        isEnabledSearch: PropTypes.bool,
        searchFormConfig: PropTypes.object,
        onSearch: PropTypes.func,
    }),
    filterConfig: PropTypes.shape({
        isEnabledFilter: PropTypes.bool,
        filterFormConfig: PropTypes.func,
        onFilterSearch: PropTypes.func,
        filterbuttonConfig: PropTypes.object,
    }),
    exportConfig: PropTypes.shape({
        isEnabledExport: PropTypes.bool,
        onExport: PropTypes.func,
        exportLabel: PropTypes.string,
        isLoading: PropTypes.bool,
        exportOptions: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string.isRequired,
                icon: PropTypes.string,
                onClick: PropTypes.func,
            })
        ),
    }),
    customButton: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            variant: PropTypes.string,
            ttLabel: PropTypes.string,
            targetId: PropTypes.string,
            onClick: PropTypes.func,
            leftIcon: PropTypes.string,
            rightIcon: PropTypes.string,
            iconWidth: PropTypes.number,
            size: PropTypes.string,
            loading: PropTypes.bool,
            className: PropTypes.string,
        })
    ),
};